import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Header } from '../components/Header';
import { buildApiUrl } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

type CampaignContactMessage = {
  id: string;
  contactId?: string | null;
  contactName?: string | null;
  contactPhone?: string | null;
  status?: string | null;
  sessionName?: string | null;
  sentAt?: string | null;
  errorMessage?: string | null;
  contactEmail?: string | null;
  contactDocument?: string | null;
  contactAddress?: string | null;
  contactProcess?: string | null;
  contactMandate?: string | null;
  contactCategory?: string | null;
  contactNotes?: string | null;
  mandateId?: string | null;
  mandateRecipientName?: string | null;
  mandateAddress?: string | null;
  mandateStatus?: string | null;
  mandateCaseNumber?: string | null;
  mandateType?: string | null;
};

type CampaignReportData = {
  campaign: {
    id: string;
    nome: string;
    criadoEm: string;
    startedAt?: string | null;
    completedAt?: string | null;
    status: string;
    messages: CampaignContactMessage[];
  };
  stats: {
    total: number;
    sent: number;
    failed: number;
    pending: number;
  };
};

type StatusTone = 'success' | 'warning' | 'danger';

const getStatusMeta = (status?: string | null) => {
  const normalized = (status || '').toUpperCase();
  if (['SENT', 'DELIVERED', 'READ'].includes(normalized)) {
    return { label: 'Enviado', tone: 'success' as StatusTone, icon: '✓' };
  }
  if (normalized === 'FAILED') {
    return { label: 'Falha', tone: 'danger' as StatusTone, icon: '!' };
  }
  return { label: 'Pendente', tone: 'warning' as StatusTone, icon: '…' };
};

const getToneClasses = (tone: StatusTone) => {
  if (tone === 'success') return 'bg-emerald-100 text-emerald-800 ring-1 ring-emerald-200';
  if (tone === 'danger') return 'bg-rose-100 text-rose-800 ring-1 ring-rose-200';
  return 'bg-amber-100 text-amber-800 ring-1 ring-amber-200';
};

const getProgressBarClass = (tone: StatusTone) => {
  if (tone === 'success') return 'bg-emerald-500';
  if (tone === 'danger') return 'bg-rose-500';
  return 'bg-amber-500';
};

export function CampaignContactsPage() {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();

  const [loading, setLoading] = useState(true);
  const [reportData, setReportData] = useState<CampaignReportData | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'sent' | 'pending' | 'failed'>('all');
  const [selectedMessage, setSelectedMessage] = useState<CampaignContactMessage | null>(null);

  const handleCertifyContact = (message: CampaignContactMessage) => {
    navigate('/certificacao', {
      state: {
        fromCampaignContact: {
          contactName: message.contactName || 'Contato sem nome',
          contactPhone: message.contactPhone || '',
          contactEmail: message.contactEmail || '',
          contactDocument: message.contactDocument || '',
          contactAddress: message.contactAddress || '',
          contactProcess: message.contactProcess || '',
          contactMandate: message.contactMandate || '',
          contactCategory: message.contactCategory || '',
          contactNotes: message.contactNotes || '',
        }
      }
    });
  };

  const loadReport = useCallback(async () => {
    if (!campaignId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(buildApiUrl(`/campaigns/${campaignId}/report`), {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        }
      });

      if (!response.ok) {
        if (response.status === 404) {
          toast.error('Esta campanha nao existe mais. Voltando para a lista.');
          navigate('/campanhas', { replace: true });
          return;
        }

        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      setReportData(data);
    } catch (error) {
      console.error('Erro ao carregar lista de contatos:', error);
      toast.error('Erro ao carregar lista de contatos');
      setReportData(null);
    } finally {
      setLoading(false);
    }
  }, [campaignId, navigate, token]);

  useEffect(() => {
    void loadReport();
  }, [loadReport]);

  const messages = reportData?.campaign?.messages || [];

  const normalizedMessages = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();

    return messages.filter((message) => {
      const meta = getStatusMeta(message.status);
      const matchesStatus =
        statusFilter === 'all' ||
        (statusFilter === 'sent' && meta.tone === 'success') ||
        (statusFilter === 'pending' && meta.tone === 'warning') ||
        (statusFilter === 'failed' && meta.tone === 'danger');

      const haystack = [
        message.contactName,
        message.contactPhone,
        message.contactEmail,
        message.contactDocument
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      const matchesSearch = !query || haystack.includes(query);

      return matchesStatus && matchesSearch;
    });
  }, [messages, searchTerm, statusFilter]);

  const summary = useMemo(() => {
    const total = reportData?.stats?.total ?? messages.length;
    const sent = reportData?.stats?.sent ?? messages.filter((message) => getStatusMeta(message.status).tone === 'success').length;
    const failed = reportData?.stats?.failed ?? messages.filter((message) => getStatusMeta(message.status).tone === 'danger').length;
    const pending = reportData?.stats?.pending ?? Math.max(total - sent - failed, 0);
    return { total, sent, failed, pending };
  }, [messages, reportData]);

  const progressTone: StatusTone = summary.failed > 0 && summary.sent === 0 ? 'danger' : summary.pending > 0 ? 'warning' : 'success';
  const progressValue = summary.total > 0 ? Math.min(100, Math.round((summary.sent / summary.total) * 100)) : 0;

  const dateLabel = useMemo(() => {
    if (!reportData?.campaign?.criadoEm) return 'Data indisponível';
    return new Date(reportData.campaign.criadoEm).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  }, [reportData?.campaign?.criadoEm]);

  return (
    <>
      <Header
        title="Lista de Contatos"
        subtitle={reportData?.campaign?.nome || 'Detalhes do envio'}
        actions={
          <button
            type="button"
            onClick={() => navigate('/campanhas')}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
          >
            <span aria-hidden="true">←</span>
            Voltar
          </button>
        }
      />

      <div className="min-h-[calc(100vh-80px)] bg-slate-50 px-4 py-6 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl space-y-6">
          <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
              <div className="space-y-2">
                <div className="text-sm font-medium text-slate-500">{dateLabel}</div>
                <h2 className="text-2xl font-semibold text-slate-900 sm:text-3xl">
                  {reportData?.campaign?.nome || 'Carregando envio...'}
                </h2>
                <p className="text-sm text-slate-500">
                  {summary.total} contatos • {summary.sent} enviados • {summary.pending} pendentes • {summary.failed} falhas
                </p>
              </div>

              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                  <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Total</p>
                  <p className="mt-2 text-2xl font-semibold text-slate-900">{summary.total}</p>
                </div>
                <div className="rounded-2xl border border-emerald-200 bg-emerald-50/80 px-4 py-3">
                  <p className="text-xs font-semibold uppercase tracking-[0.16em] text-emerald-800/80">Enviados</p>
                  <p className="mt-2 text-2xl font-semibold text-emerald-900">{summary.sent}</p>
                </div>
                <div className="rounded-2xl border border-amber-200 bg-amber-50/80 px-4 py-3">
                  <p className="text-xs font-semibold uppercase tracking-[0.16em] text-amber-800/80">Pendentes</p>
                  <p className="mt-2 text-2xl font-semibold text-amber-900">{summary.pending}</p>
                </div>
                <div className="rounded-2xl border border-rose-200 bg-rose-50/80 px-4 py-3">
                  <p className="text-xs font-semibold uppercase tracking-[0.16em] text-rose-800/80">Falhas</p>
                  <p className="mt-2 text-2xl font-semibold text-rose-900">{summary.failed}</p>
                </div>
              </div>
            </div>

            <div className="mt-6 space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium text-slate-700">Progresso geral</span>
                <span className="text-slate-500">{summary.sent}/{summary.total}</span>
              </div>
              <div className="h-3 overflow-hidden rounded-full bg-slate-100">
                <div
                  className={`h-full rounded-full ${getProgressBarClass(progressTone)} transition-all duration-700 ease-out`}
                  style={{ width: `${progressValue}%` }}
                />
              </div>
            </div>
          </section>

          <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_240px]">
              <label className="block">
                <span className="mb-2 block text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
                  Buscar
                </span>
                <input
                  type="search"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Nome ou telefone..."
                  className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-blue-400 focus:bg-white focus:ring-4 focus:ring-blue-100"
                />
              </label>

              <label className="block">
                <span className="mb-2 block text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
                  Status
                </span>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}
                  className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-blue-400 focus:bg-white focus:ring-4 focus:ring-blue-100"
                >
                  <option value="all">Todos</option>
                  <option value="sent">Enviado</option>
                  <option value="pending">Pendente</option>
                  <option value="failed">Falha</option>
                </select>
              </label>
            </div>
          </section>

          <section className="space-y-4">
            {loading ? (
              <div className="rounded-3xl border border-dashed border-slate-300 bg-white px-6 py-16 text-center text-slate-500">
                Carregando contatos...
              </div>
            ) : normalizedMessages.length === 0 ? (
              <div className="rounded-3xl border border-dashed border-slate-300 bg-white px-6 py-16 text-center text-slate-500">
                Nenhum contato encontrado
              </div>
            ) : (
              <div className="grid gap-4">
                {normalizedMessages.map((message) => {
                  const meta = getStatusMeta(message.status);
                  return (
                    <article
                      key={message.id}
                      className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
                    >
                      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-3">
                            <span className={`inline-flex h-9 w-9 items-center justify-center rounded-full ${getToneClasses(meta.tone)}`}>
                              {meta.icon}
                            </span>
                            <div className="min-w-0">
                              <h3 className="truncate text-base font-semibold text-slate-900">
                                {message.contactName || 'Contato sem nome'}
                              </h3>
                              <p className="text-sm text-slate-500">{message.contactPhone || 'Telefone indisponível'}</p>
                            </div>
                            <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${getToneClasses(meta.tone)}`}>
                              {meta.label}
                            </span>
                            {message.errorMessage && meta.tone === 'danger' && (
                              <span
                                className="inline-flex items-center rounded-full border border-rose-200 bg-rose-50 px-3 py-1 text-xs text-rose-700"
                                title={message.errorMessage}
                              >
                                Erro ao enviar
                              </span>
                            )}
                          </div>

                          <div className="mt-3 flex flex-wrap gap-2 text-xs text-slate-500">
                            <span>Sessão: {message.sessionName || 'N/A'}</span>
                            <span>•</span>
                            <span>Envio: {message.sentAt ? new Date(message.sentAt).toLocaleString('pt-BR') : 'Aguardando'}</span>
                          </div>
                        </div>

                        <div className="flex flex-col gap-2 sm:flex-row lg:min-w-[360px] lg:justify-end">
                          <button
                            type="button"
                            onClick={() => handleCertifyContact(message)}
                            className="inline-flex items-center justify-center rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-2.5 text-sm font-semibold text-emerald-700 transition hover:border-emerald-300 hover:bg-emerald-100 active:scale-[0.98]"
                          >
                            Certificar
                          </button>
                          <button
                            type="button"
                            onClick={() => setSelectedMessage(message)}
                            className="inline-flex items-center justify-center rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-blue-700 active:scale-[0.98]"
                          >
                            Ver detalhes
                          </button>
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
            )}
          </section>
        </div>
      </div>

      {selectedMessage && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-slate-950/50 p-4 backdrop-blur-sm">
          <div className="w-full max-w-2xl rounded-3xl bg-white shadow-2xl">
            <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-6 py-5">
              <div>
                <h3 className="text-lg font-semibold text-slate-900">Detalhes do contato</h3>
                <p className="text-sm text-slate-500">{selectedMessage.contactName || 'Contato sem nome'}</p>
              </div>
              <button
                type="button"
                onClick={() => setSelectedMessage(null)}
                className="rounded-full p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
                aria-label="Fechar"
              >
                ✕
              </button>
            </div>

            <div className="space-y-4 px-6 py-5">
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Telefone</p>
                  <p className="mt-1 text-sm text-slate-900">{selectedMessage.contactPhone || 'Não informado'}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Status</p>
                  <p className="mt-1 text-sm text-slate-900">{getStatusMeta(selectedMessage.status).label}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Sessão</p>
                  <p className="mt-1 text-sm text-slate-900">{selectedMessage.sessionName || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Data de envio</p>
                  <p className="mt-1 text-sm text-slate-900">
                    {selectedMessage.sentAt ? new Date(selectedMessage.sentAt).toLocaleString('pt-BR') : 'Aguardando'}
                  </p>
                </div>
              </div>

              {selectedMessage.errorMessage && (
                <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4">
                  <p className="text-xs font-semibold uppercase tracking-[0.16em] text-rose-700">Erro</p>
                  <p className="mt-2 text-sm text-rose-800">{selectedMessage.errorMessage}</p>
                </div>
              )}

              <div className="grid gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-4 sm:grid-cols-2">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Email</p>
                  <p className="mt-1 text-sm text-slate-900">{selectedMessage.contactEmail || 'Não informado'}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Documento</p>
                  <p className="mt-1 text-sm text-slate-900">{selectedMessage.contactDocument || 'Não informado'}</p>
                </div>
                <div className="sm:col-span-2">
                  <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Endereço</p>
                  <p className="mt-1 text-sm text-slate-900">{selectedMessage.contactAddress || 'Não informado'}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Processo</p>
                  <p className="mt-1 text-sm text-slate-900">{selectedMessage.contactProcess || 'Não informado'}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Mandado</p>
                  <p className="mt-1 text-sm text-slate-900">{selectedMessage.contactMandate || 'Não informado'}</p>
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => handleCertifyContact(selectedMessage)}
                  className="inline-flex items-center justify-center rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-2.5 text-sm font-semibold text-emerald-700 transition hover:border-emerald-300 hover:bg-emerald-100 active:scale-[0.98]"
                >
                  Certificar este contato
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
